import { Reducer } from "redux";

export interface Tab {
  id: string;
  path: string;
  title: string;
  icon?: string;
  closable: boolean;
}

export interface TabsState {
  tabs: Tab[];
  activeTabId: string;
}

const MAX_TABS = 15;

const INITIAL_STATE: TabsState = {
  tabs: [
    {
      id: '/home',
      path: '/home',
      title: 'Dashboard',
      icon: 'dashboard',
      closable: false,
    },
  ],
  activeTabId: '/home',
};

export const TabsTypes = {
  OPEN_TAB: 'TABS/OPEN_TAB',
  CLOSE_TAB: 'TABS/CLOSE_TAB',
  SET_ACTIVE_TAB: 'TABS/SET_ACTIVE_TAB',
  CLOSE_OTHER_TABS: 'TABS/CLOSE_OTHER_TABS',
  CLOSE_TABS_RIGHT: 'TABS/CLOSE_TABS_RIGHT',
  CLOSE_ALL_TABS: 'TABS/CLOSE_ALL_TABS',
  REORDER_TABS: 'TABS/REORDER_TABS',
  CLEAR_TABS: 'TABS/CLEAR_TABS',
  FIX_TAB: 'TABS/FIX_TAB',
};

export const TabsCreators = {
  openTab: (tab: Tab) => ({
    type: TabsTypes.OPEN_TAB,
    tab,
  }),
  closeTab: (tabId: string) => ({
    type: TabsTypes.CLOSE_TAB,
    tabId,
  }),
  setActiveTab: (tabId: string) => ({
    type: TabsTypes.SET_ACTIVE_TAB,
    tabId,
  }),
  closeOtherTabs: (tabId: string) => ({
    type: TabsTypes.CLOSE_OTHER_TABS,
    tabId,
  }),
  closeTabsRight: (tabId: string) => ({
    type: TabsTypes.CLOSE_TABS_RIGHT,
    tabId,
  }),
  closeAllTabs: () => ({
    type: TabsTypes.CLOSE_ALL_TABS,
  }),
  reorderTabs: (fromIndex: number, toIndex: number) => ({
    type: TabsTypes.REORDER_TABS,
    fromIndex,
    toIndex,
  }),
  clearTabs: () => ({
    type: TabsTypes.CLEAR_TABS,
  }),
  fixTab: (tabId: string) => ({
    type: TabsTypes.FIX_TAB,
    tabId,
  }),
};

const tabsReducer: Reducer<TabsState> = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case TabsTypes.OPEN_TAB: {
      const existing = state.tabs.find((t) => t.id === action.tab.id);
      if (existing) {
        return { ...state, activeTabId: action.tab.id };
      }
      if (state.tabs.length >= MAX_TABS) {
        // Fecha a tab closable mais antiga para abrir a nova
        const oldestClosable = state.tabs.find((t) => t.closable);
        if (oldestClosable) {
          const trimmed = state.tabs.filter((t) => t.id !== oldestClosable.id);
          return {
            ...state,
            tabs: [...trimmed, action.tab],
            activeTabId: action.tab.id,
          };
        }
        return state;
      }
      return {
        ...state,
        tabs: [...state.tabs, action.tab],
        activeTabId: action.tab.id,
      };
    }

    case TabsTypes.CLOSE_TAB: {
      const tabToClose = state.tabs.find((t) => t.id === action.tabId);
      if (!tabToClose || !tabToClose.closable) return state;

      const newTabs = state.tabs.filter((t) => t.id !== action.tabId);
      let newActiveId = state.activeTabId;

      if (state.activeTabId === action.tabId) {
        const closedIndex = state.tabs.findIndex((t) => t.id === action.tabId);
        const nextTab = newTabs[closedIndex] || newTabs[closedIndex - 1] || newTabs[0];
        newActiveId = nextTab ? nextTab.id : '/home';
      }

      return { ...state, tabs: newTabs, activeTabId: newActiveId };
    }

    case TabsTypes.SET_ACTIVE_TAB:
      return { ...state, activeTabId: action.tabId };

    case TabsTypes.CLOSE_OTHER_TABS: {
      const kept = state.tabs.filter((t) => !t.closable || t.id === action.tabId);
      return { ...state, tabs: kept, activeTabId: action.tabId };
    }

    case TabsTypes.CLOSE_TABS_RIGHT: {
      const idx = state.tabs.findIndex((t) => t.id === action.tabId);
      const kept = state.tabs.filter((t, i) => i <= idx || !t.closable);
      const activeStillExists = kept.some((t) => t.id === state.activeTabId);
      return {
        ...state,
        tabs: kept,
        activeTabId: activeStillExists ? state.activeTabId : action.tabId,
      };
    }

    case TabsTypes.CLOSE_ALL_TABS: {
      const fixed = state.tabs.filter((t) => !t.closable);
      return { ...state, tabs: fixed, activeTabId: fixed[0]?.id || '/home' };
    }

    case TabsTypes.REORDER_TABS: {
      const reordered = [...state.tabs];
      const [moved] = reordered.splice(action.fromIndex, 1);
      reordered.splice(action.toIndex, 0, moved);
      return { ...state, tabs: reordered };
    }

    case TabsTypes.CLEAR_TABS:
      return INITIAL_STATE;

    case TabsTypes.FIX_TAB:
      return {
        ...state,
        // Só a tela inicial (e o dashboard) ficam fixas: a aba que deixou de ser a inicial volta a fechar.
        tabs: state.tabs.map((t) => (t.id === '/home' ? t : { ...t, closable: t.id !== action.tabId })),
      };

    default:
      return state;
  }
};

export default tabsReducer;
